import { useCallback } from 'react';
import { useRouter } from 'next/router';
import cn from 'classnames';
import MissingSomethingModalButton from '@/creditflowresearch/ui/common/components/MissingSomethingModalButton';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { IMarketCategoriesData } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Stats/IMarketCategory';
import { IMarketStatsData } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Stats/IMarketStat';
import CheckUserAccess from '@/creditflowresearch/ui/common/components/CheckUserAccess';
import NoAccessOverlayStats from '@/creditflowresearch/ui/components/creditFlow/common/NoAccessOverlayStats';
import StatsContextProvider from '@/creditflowresearch/application/ArticlesAndStats/Stats/StatsContext';
import StatsList from '@/creditflowresearch/ui/components/creditFlow/Stats/StatsList';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import PageMeta from '@/creditflowresearch/ui/common/components/PageMeta';
import getStatsMetaSchema from './getStatsMetaSchema';
import ProductPageHeader from '@/creditflowresearch/ui/common/components/templates/ProductPageHeader';
import getStatsBreadcrumbs from '@/creditflowresearch/ui/components/creditFlow/Stats/getStatsBreadcrumbs';
import { useBreadcrumbs } from '@/creditflowresearch/ui/common/hooks';
import { IWithStatsConfigProps } from './withStatsConfig';
import { UserPlanFeature } from '@creditflowresearch/user-plan/vo/IUserPlanResponse';
import URL from '@/creditflowresearch/Router/urlConstants';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';

import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';

interface IStatsBaseProps {
  categoriesData: IMarketCategoriesData,
  statsData: IMarketStatsData,
}

interface IStatsProps extends IStatsBaseProps, IWithStatsConfigProps {}

const Stats = ({ statsData, categoriesData, statsConfig }: IStatsProps) => {
  const {
    checkAccessByUserProducts,
    checkAccessByUserMarket,
  } = usePageFiltersContext();
  const title = statsConfig.title || 'Stats';
  const titleWithPrefix = statsConfig.titlePrefix + title;
  const {
    checkAccessByUserPlan,
    checkAccessByFeature,
  } = useUserPlanContext();

  const granted = useAccessGrant();

  const checks = [
    granted || checkAccessByUserPlan(UserPlanType.PREMIUM),
    granted || checkAccessByUserMarket(),
    granted || checkAccessByUserProducts(),
    granted || checkAccessByFeature(UserPlanFeature.NewIssueStats),
  ];
  const { asPath: pathname, query, replace } = useRouter();
  const { products, regions } = query;

  useBreadcrumbs(
    () => getStatsBreadcrumbs(query),
    [query],
  );

  const onFilterToggle = useCallback((searchQuery: string): void => {
    replace(`${ URL.CREDIT_FLOW_STATS_LIST_ALL }?${ searchQuery }`, undefined, { shallow: true });
  }, [replace]);

  return (
    <>
      <PageMeta
        title={ titleWithPrefix }
        schema={ getStatsMetaSchema(pathname, products as string, regions as string) }
      />
      <ProductPageHeader
        title={ title }
        items={ statsConfig.headerFilters }
        accessConditions={ [{ checks }] }
        blockingAccessConditions={ [{ checks: [granted || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
        panelBackground={ statsConfig.panelBackground }
        onToggle={ onFilterToggle }
      />

      <CheckUserAccess conditions={ [{ checks, fallback: <NoAccessOverlayStats /> }] }>
        <StatsContextProvider
          categoriesData={ categoriesData }
          statsData={ statsData }
          titleWithPrefix={ titleWithPrefix }
          filterCategory={ statsConfig.filterCategory }
        >
          <StatsList columns={ statsConfig.columns } />
        </StatsContextProvider>
      </CheckUserAccess>

      <MissingSomethingModalButton className={ cn(spacesStyles.ptxl, spacesStyles.pbxl) } />
    </>
  );
};

export default Stats;
